import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const sections = [
  {
    title: "1. Titolare del trattamento",
    body: [
      "Il titolare del trattamento dei dati personali è FuffyFam (di seguito «FuffyFam» o «il Titolare»), raggiungibile all'indirizzo email supporto.fuffyfam@gmail.com.",
      "La presente informativa è resa ai sensi del Regolamento (UE) 2016/679 («GDPR») e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018, e descrive come raccogliamo, usiamo e proteggiamo i dati degli utenti dell'app FuffyFam e del sito fuffyfam.com.",
    ],
  },
  {
    title: "2. Dati che raccogliamo",
    list: [
      "Dati dell'account: nome, indirizzo email, numero di telefono e foto profilo, forniti al momento della registrazione.",
      "Dati del cane: nome, razza, data di nascita, peso, foto, libretto sanitario digitale, vaccinazioni, visite veterinarie e altre informazioni inserite volontariamente.",
      "Posizione GPS: raccolta solo previa autorizzazione, per mostrarti cani e proprietari nelle vicinanze, sitter e strutture dog-friendly nella tua zona. Puoi revocarla in qualsiasi momento dalle impostazioni del dispositivo.",
      "Dati di utilizzo e dispositivo: modello del dispositivo, sistema operativo, identificatori tecnici, log di accesso e interazioni con l'app, raccolti in forma aggregata tramite strumenti di analytics.",
      "Dati della waitlist: l'indirizzo email lasciato su fuffyfam.com per essere informato sul lancio.",
    ],
  },
  {
    title: "3. Come usiamo i tuoi dati",
    list: [
      "Creare e gestire il tuo account e il profilo del tuo cane.",
      "Fornire le funzioni dell'app: promemoria per vaccini e scadenze sanitarie, ricerca di sitter e strutture, incontro con altri proprietari nella zona.",
      "Inviarti comunicazioni di servizio e, solo con il tuo consenso, novità e offerte.",
      "Migliorare l'app tramite analisi aggregate e anonime sull'utilizzo.",
      "Garantire la sicurezza del servizio e prevenire abusi.",
    ],
    body: [
      "La base giuridica del trattamento è l'esecuzione del contratto (erogazione del servizio), il consenso (posizione GPS, marketing, foto) e il legittimo interesse (sicurezza e miglioramento del servizio).",
    ],
  },
  {
    title: "4. Con chi condividiamo i dati",
    body: [
      "Non vendiamo mai i tuoi dati personali. Li condividiamo solo con:",
    ],
    list: [
      "Google Firebase: autenticazione, database, notifiche push e analytics. I dati sono trattati secondo le condizioni di Google Firebase e nel rispetto del GDPR.",
      "Google Play Store e Apple App Store: per la distribuzione dell'app e la gestione di eventuali acquisti in-app.",
      "Partner del servizio (dog sitter, strutture ricettive, negozi convenzionati): solo i dati strettamente necessari e solo quando scegli tu di contattarli o prenotare tramite l'app.",
      "Autorità pubbliche, solo quando richiesto dalla legge.",
    ],
  },
  {
    title: "5. Conservazione dei dati",
    body: [
      "Conserviamo i tuoi dati per tutto il tempo in cui il tuo account è attivo. I dati della waitlist sono conservati fino al lancio dell'app o fino alla tua richiesta di cancellazione.",
      "Dopo la cancellazione dell'account, i dati personali vengono eliminati o anonimizzati entro 30 giorni, salvo obblighi di legge che ne impongano la conservazione.",
    ],
  },
  {
    title: "6. I tuoi diritti",
    body: [
      "In qualità di interessato hai diritto a:",
    ],
    list: [
      "Accedere ai tuoi dati e riceverne copia (diritto di accesso e portabilità).",
      "Rettificare dati inesatti o incompleti.",
      "Cancellare il tuo account e tutti i dati associati («diritto all'oblio»): puoi farlo direttamente dall'app (Profilo → Impostazioni → Elimina account) o scrivendo a supporto.fuffyfam@gmail.com.",
      "Revocare in qualsiasi momento il consenso alla geolocalizzazione e alle comunicazioni di marketing.",
      "Opporti al trattamento o chiederne la limitazione.",
      "Proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
  {
    title: "7. Sicurezza",
    body: [
      "Adottiamo misure tecniche e organizzative adeguate: connessioni cifrate (HTTPS/TLS), accesso ai dati limitato al personale autorizzato e infrastrutture cloud conformi agli standard di settore.",
    ],
  },
  {
    title: "8. Minori",
    body: [
      "FuffyFam non è rivolta a minori di 16 anni. Non raccogliamo consapevolmente dati di minori; se veniamo a conoscenza di una registrazione effettuata da un minore, provvediamo alla cancellazione immediata.",
    ],
  },
  {
    title: "9. Modifiche a questa informativa",
    body: [
      "Potremmo aggiornare questa informativa per adeguarla all'evoluzione del servizio o della normativa. In caso di modifiche sostanziali ti avviseremo tramite l'app o via email. Ultimo aggiornamento: luglio 2026.",
    ],
  },
  {
    title: "10. Contatti",
    body: [
      "Per qualsiasi richiesta relativa alla privacy — accesso, cancellazione, rettifica o domande — scrivici a supporto.fuffyfam@gmail.com. Rispondiamo entro 30 giorni, come previsto dal GDPR.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" data-testid="privacy-page">
      <nav className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6 border-b border-[#1A1A1A]/[0.06]">
        <Logo testId="privacy-logo" />
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="flex items-center gap-2 text-sm font-semibold text-[#5C5C5C] hover:text-[#FF6B35] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>
      </nav>

      <main className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B35] mb-6">
            <ShieldCheck className="w-4 h-4" />
            Documento legale
          </p>
          <h1
            data-testid="privacy-title"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            Privacy Policy
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            La tua privacy — e quella del tuo cane — ci sta a cuore. Qui spieghiamo in modo
            chiaro quali dati raccogliamo, perché, e quali diritti hai. Ultimo aggiornamento: luglio 2026.
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {sections.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: 0.05 * (i % 3), ease: [0.16, 1, 0.3, 1] }}
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
                {s.title}
              </h2>
              {s.body?.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-[#5C5C5C]">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-3">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex gap-3 text-base leading-relaxed text-[#5C5C5C]">
                      <span className="mt-[0.6rem] w-1.5 h-1.5 rounded-full bg-[#FF6B35] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#1A1A1A]/[0.08] px-6 sm:px-10 lg:px-16 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto">
        <p className="text-sm text-[#5C5C5C]">© 2026 FuffyFam · fuffyfam.com</p>
        <a
          href="mailto:supporto.fuffyfam@gmail.com"
          data-testid="privacy-contact-link"
          className="text-sm font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors duration-300"
        >
          supporto.fuffyfam@gmail.com
        </a>
      </footer>
    </div>
  );
}
